import { useState, useRef, useEffect, useCallback } from "react";
import _ from "lodash";
import { useDispatch } from "Hooks/useDispatch";
import { getTextToSpeechAudio } from "modules/Services/ServicesThunks";
import { getDefaultTTSModel } from "modules/Services/ServicesUtils";

const MODEL_TO_OUTPUT_FORMAT_MAP = {
  "toddle-tts-v1": "audio/aac",
  "toddle-tts-v2": "audio/aac",
  "toddle-tts-v3": "audio/aac",
  "toddle-tts-v4": "audio/aac",
  "toddle-tts-v5": "audio/mpeg",
  "toddle-tts-v6": "audio/mpeg",
};

const useTextToSpeech = ({ onAudioURLReceived, model }) => {
  const defaultModel = getDefaultTTSModel();
  const [
    currentlyAudioPlayingForItemId,
    setCurrentlyAudioPlayingForItemId,
  ] = useState(null);
  const [isAudioGenerating, setIsAudioGenerating] = useState(false);
  const [requestController, setRequestController] = useState(null);
  const dispatch = useDispatch();

  // Audio control and buffer, streaming related refs
  const isAudioStreamingFinished = useRef(false);
  const audioRef = useRef(null); // Ref for <audio> tag
  const mediaSourceRef = useRef(null); // Ref for MediaSource
  const bufferRef = useRef(null); // Ref for MediaSource buffer
  const queueRef = useRef([]); // Queue to hold streammed audio chunks until buffer is ready to take it and play.

  // Cleanup media source and related refs
  const cleanupMediaSource = useCallback(() => {
    mediaSourceRef.current = null;
    bufferRef.current = null;
    queueRef.current = [];
  }, [mediaSourceRef]);

  // Initialize audio element
  useEffect(() => {
    // Create audio element programmatically
    audioRef.current = new Audio();
    audioRef.current.style.display = "none";
    audioRef.current.style.pointerEvents = "none";

    // Append to body for proper functioning
    document.body.appendChild(audioRef.current);

    return () => {
      // Cleanup on unmount
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
        if (audioRef.current.parentNode) {
          audioRef.current.parentNode.removeChild(audioRef.current);
        }
      }
      cleanupMediaSource();
    };
  }, []);

  // Handle audio playback ended
  const handleAudioEnded = useCallback(() => {
    setCurrentlyAudioPlayingForItemId(null);
    setIsAudioGenerating(false);
    isAudioStreamingFinished.current = false;
    setRequestController(null);

    if (audioRef.current) {
      audioRef.current.src = "";
      audioRef.current.removeEventListener("waiting", () => {});
      audioRef.current.removeEventListener("ended", () => {});
    }

    cleanupMediaSource();
  }, [cleanupMediaSource]);

  // When the stream is complete, properly end the MediaSource to indicate no more data will be added
  const finalizeStream = useCallback(() => {
    try {
      if (mediaSourceRef.current?.readyState === "open") {
        mediaSourceRef.current.endOfStream();
        // Now the audio element will fire 'ended' event when it reaches the end
        audioRef.current?.addEventListener("ended", handleAudioEnded);
      }
    } catch (error) {}
  }, [handleAudioEnded]);

  // Update buffer with queued audio chunks
  const updateBuffer = useCallback(() => {
    if (
      !bufferRef.current ||
      !mediaSourceRef.current ||
      mediaSourceRef.current.readyState !== "open"
    ) {
      return;
    }

    // Take next chunk from queue if buffer is ready.
    if (queueRef.current.length > 0 && !bufferRef.current.updating) {
      const chunk = queueRef.current.shift();
      try {
        bufferRef.current.appendBuffer(chunk);
      } catch (error) {}
    }
  }, [bufferRef, queueRef, mediaSourceRef]);

  // Handle MediaSource open event
  const handleSourceOpen = useCallback(() => {
    if (mediaSourceRef.current.readyState !== "open") {
      return;
    }

    try {
      bufferRef.current = mediaSourceRef.current.addSourceBuffer(
        _.get(MODEL_TO_OUTPUT_FORMAT_MAP, model || defaultModel, "audio/aac")
      );
      bufferRef.current.mode = "sequence";

      bufferRef.current.addEventListener("updateend", updateBuffer);
    } catch (error) {}
  }, [updateBuffer]);

  const handleAudioWaiting = useCallback(() => {
    // If audio is waiting and streaming is finished but queue is empty, finalize stream
    if (
      isAudioStreamingFinished.current &&
      queueRef.current.length === 0 &&
      audioRef?.current?.currentTime > 0
    ) {
      finalizeStream();
    }
  }, [finalizeStream, isAudioStreamingFinished, queueRef]);

  // Initialize MediaSource for streaming
  const initMediaSource = useCallback(() => {
    const audio = audioRef.current;
    const mediaSource = new MediaSource();
    mediaSourceRef.current = mediaSource;

    audio.src = URL.createObjectURL(mediaSource);

    // Add event listeners
    // When media source is open, we can start adding buffer
    mediaSource.addEventListener("sourceopen", handleSourceOpen);
    audioRef.current.addEventListener("waiting", handleAudioWaiting);
    // When waiting state, check if audio has finished or waiting for more data
  }, [handleSourceOpen]);

  const processAudioChunk = useCallback(
    chunk => {
      if (_.isNil(chunk) || typeof chunk?.data !== "object") {
        return;
      }

      const typedArray = new Uint8Array(chunk.data);
      const arrayBuffer = typedArray.buffer;

      // Queue or append buffer based on current state
      if (
        !bufferRef.current ||
        bufferRef.current.updating ||
        queueRef.current.length > 0
      ) {
        queueRef.current.push(arrayBuffer);
      } else {
        try {
          bufferRef.current.appendBuffer(arrayBuffer);
        } catch {
          queueRef.current.push(arrayBuffer);
        }
      }
    },
    [bufferRef, queueRef]
  );

  const streamAudioSpeech = useCallback(
    ({ audioStream, index, itemId }) => {
      const len = audioStream?.length;

      while (index < len) {
        if (_.isEmpty(audioRef.current?.src)) break;

        if (index >= 2 && audioRef.current.currentTime === 0) {
          if (
            _.isEmpty(audioRef.current.src) ||
            _.isNil(audioRef.current.src)
          ) {
            audioRef.current.src = URL.createObjectURL(mediaSourceRef?.current);
          }
          audioRef.current.play();
          setCurrentlyAudioPlayingForItemId(itemId);
          setIsAudioGenerating(false);
        }

        const chunk = audioStream?.[index]?.chunk;
        processAudioChunk(chunk);
        index++;
      }
      return index;
    },
    [processAudioChunk]
  );

  // Main function to handle speak button click
  const handleSpeakClick = useCallback(
    async ({ itemId, entityType, content, audioURL }) => {
      // Pause if already playing this item
      if (currentlyAudioPlayingForItemId === itemId) {
        audioRef.current?.pause();
        requestController?.abort?.();
        finalizeStream();
        handleAudioEnded();
        return;
      }

      // Stop previous audio if another item is playing
      if (!_.isNil(currentlyAudioPlayingForItemId)) {
        audioRef.current?.pause();
        audioRef.current.currentTime = 0;
        requestController?.abort?.();
        finalizeStream();
        handleAudioEnded();
      }

      // Play existing audio if available
      if (!_.isEmpty(audioURL)) {
        setCurrentlyAudioPlayingForItemId(itemId);
        audioRef.current.src = audioURL;
        audioRef.current.addEventListener("ended", handleAudioEnded);
        await audioRef.current.play();
        return;
      }

      //Else Generate new audio from TTS service
      setIsAudioGenerating(true);
      setCurrentlyAudioPlayingForItemId(itemId);
      initMediaSource();
      let streamChunkIndex = 0;

      try {
        const streamResponse = await dispatch(
          getTextToSpeechAudio({
            inputText: content,
            entityType: entityType || "PLATFORM_TTS",
            entityId: itemId,
            onDownloadProgress: responseChunk => {
              streamChunkIndex = streamAudioSpeech({
                audioStream: responseChunk,
                index: streamChunkIndex,
                itemId,
              });
            },
            setRequestController: controller =>
              setRequestController(controller),
            model: model,
          })
        );
        isAudioStreamingFinished.current = true;
        // Note: finalizeStream will be called automatically by updateBuffer when queue is empty

        // Save audio URL if update function is provided
        const speechUrl = _.get(streamResponse, "speechUrl", "");
        if (!_.isEmpty(speechUrl) && _.isFunction(onAudioURLReceived)) {
          await onAudioURLReceived({ itemId, generatedAudioURL: speechUrl }); // Did "await" so that consumer component can use it for async update.
        }

        return streamResponse;
      } catch (error) {
        finalizeStream();
        handleAudioEnded();
      }
    },
    [
      requestController,
      handleAudioEnded,
      initMediaSource,
      streamAudioSpeech,
      onAudioURLReceived,
      currentlyAudioPlayingForItemId,
      dispatch,
      finalizeStream,
    ]
  );

  return {
    currentlyAudioPlayingForItemId,
    setCurrentlyAudioPlayingForItemId,
    isAudioGenerating,
    isAudioPlaying:
      !_.isNil(currentlyAudioPlayingForItemId) &&
      _.get(audioRef, "current.currentTime", 0) > 0,
    handleSpeakClick,
  };
};

export default useTextToSpeech;
